import { useState } from "react";

import {
  faCalendarCheck,
  faRuler,
  faBolt,
  faThermometerHalf
} from "@fortawesome/free-solid-svg-icons";

import useCsvUploader from "./csvUploader";

import { createErrorObj, doesFieldExist } from "../../helpers/components/inputs";

const buildConfigForSubcategory = (subcategory) => {
  if (subcategory === "purchased-water") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "Quantity", value: "quantity" },
        { label: "Unit (gallons or cubicMeters)", value: "unit" },
        { label: "Wastewater %", value: "wastewaterPercentage" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this water usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "Quantity",
          description: "How much water was used?",
          icon: faRuler
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "quantity", field: "quantity" },
        { header: "gallons", field: "quantity" },
        { header: "gallons/month", field: "quantity" },
        { header: "amount", field: "quantity" },
        { header: "unit", field: "unit" },
        { header: "units", field: "unit" },
        { header: "wastewater", field: "wastewaterPercentage" },
        { header: "wastewater percentage", field: "wastewaterPercentage" },
        { header: "wastewaterPercentage", field: "wastewaterPercentage" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "quantity")) {
          return createErrorObj("Your CSV must include a Quantity field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your purchased water usage and we'll calculate the emissions for each entry.",
      docType: "purchased-water",
      label: "Purchased Water"
    };
  }

  if (subcategory === "steam") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "Quantity", value: "quantity" },
        { label: "Unit (lbs, kgs, or tons)", value: "unit" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this steam usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "Quantity",
          description: "How much steam was used?",
          icon: faRuler
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "quantity", field: "quantity" },
        { header: "lbs", field: "quantity" },
        { header: "amount", field: "quantity" },
        { header: "unit", field: "unit" },
        { header: "units", field: "unit" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "quantity")) {
          return createErrorObj("Your CSV must include a Quantity field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your steam usage and we'll calculate the emissions for each entry.",
      docType: "steam",
      label: "Steam"
    };
  }

  if (subcategory === "chill-water") {
    return {
      fieldOptions: [
        { label: "Date", value: "dateCommon" },
        { label: "kWh", value: "kwh" }
      ],
      requiredFields: [
        {
          label: "Date",
          description: "When was this chill water usage recorded?",
          icon: faCalendarCheck
        },
        {
          label: "kWh",
          description: "How many kWh did the chiller consume?",
          icon: faBolt
        }
      ],
      knownFields: [
        { header: "date", field: "dateCommon" },
        { header: "dateCommon", field: "dateCommon" },
        { header: "statement date", field: "dateCommon" },
        { header: "kwh", field: "kwh" },
        { header: "kWh", field: "kwh" },
        { header: "chillWaterKwh", field: "kwh" },
        { header: "chill water kwh", field: "kwh" }
      ],
      fieldValidator: (headers) => {
        if (!doesFieldExist(headers, "dateCommon")) {
          return createErrorObj("Your CSV must include a Date field");
        }
        if (!doesFieldExist(headers, "kwh")) {
          return createErrorObj("Your CSV must include a kWh field");
        }
        return { success: true };
      },
      firstStepText:
        "Upload a CSV of your chill water kWh usage and we'll calculate the emissions for each entry.",
      docType: "chill-water",
      label: "Chill Water"
    };
  }

  return {
    fieldOptions: [
      { label: "Date", value: "dateCommon" },
      { label: "Quantity", value: "quantity" },
      { label: "Unit (lbs, kgs, or tons)", value: "unit" },
      { label: "Refrigerant Type (e.g. R-410A, R-134a, R-407C)", value: "refrigerantType" }
    ],
    requiredFields: [
      {
        label: "Date",
        description: "When was this refrigerant usage recorded?",
        icon: faCalendarCheck
      },
      {
        label: "Refrigerant Type",
        description: "What type of refrigerant was used? (e.g. R-410A, R-134a, HFC-125, R-22, HFO-1234yf)",
        icon: faThermometerHalf
      },
      {
        label: "Quantity",
        description: "How much refrigerant was used?",
        icon: faRuler
      }
    ],
    knownFields: [
      { header: "date", field: "dateCommon" },
      { header: "dateCommon", field: "dateCommon" },
      { header: "statement date", field: "dateCommon" },
      { header: "quantity", field: "quantity" },
      { header: "quantity leaked", field: "quantity" },
      { header: "amount", field: "quantity" },
      { header: "unit", field: "unit" },
      { header: "units", field: "unit" },
      { header: "type", field: "refrigerantType" },
      { header: "refrigerant type", field: "refrigerantType" },
      { header: "refrigerantType", field: "refrigerantType" },
      { header: "refrigerant", field: "refrigerantType" },
      { header: "type of refrigerant", field: "refrigerantType" }
    ],
    fieldValidator: (headers) => {
      if (!doesFieldExist(headers, "dateCommon")) {
        return createErrorObj("Your CSV must include a Date field");
      }
      if (!doesFieldExist(headers, "quantity")) {
        return createErrorObj("Your CSV must include a Quantity field");
      }
      if (!doesFieldExist(headers, "refrigerantType")) {
        return createErrorObj("Your CSV must include a Refrigerant Type field");
      }
      return { success: true };
    },
    firstStepText:
      "Upload a CSV of your refrigerant usage and we'll calculate the emissions for each entry.",
    docType: "refrigerants",
    label: "Refrigerants"
  };
};

// Ticket #412: office_csv is a migrated Smart Upload family now -- this hook
// only uploads the matched CSV to Storage (same shape as
// shippingCsvUploader.js/bomProductsCsvUploader.js); onNewObjectInBucket's
// own dispatch, officeCsvTaskBuilder, and draftOfficeCsvChunk.js do the
// actual row processing asynchronously, going through review before any
// transaction gets written. officeId/officeName are per-upload metadata,
// same as legacy's selectedOfficeId/selectedOfficeName -- one building's
// meters per file. subcategory is carried entirely by docType, which
// onNewObjectInBucket's OFFICE_CSV_SUBCATEGORIES dispatch reads back off the
// storage path.
const useOfficeCsvUploader = ({
  open,
  setOpen,
  selectedOfficeId,
  selectedOfficeName,
  subcategory
}) => {
  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);

  const {
    fieldOptions,
    requiredFields,
    knownFields,
    fieldValidator,
    firstStepText,
    docType
  } = buildConfigForSubcategory(subcategory);

  return useCsvUploader({
    knownFields,
    fieldOptions,
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    fieldValidator,
    docType,
    firstStepText,
    requiredFields,
    additionalMetadata: {
      officeId: selectedOfficeId || "",
      officeName: selectedOfficeName || ""
    }
  });
};

export default useOfficeCsvUploader;
